import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';

import type { Flow, PickupStrategy, DeliveryStrategy } from '@grafx-vdp/shared';
import {
  DEFAULT_SOURCE_SUFFIX_FILENAME_CONVENTION,
  resolveDeliveryStrategy,
  resolvePickupStrategy,
} from '@grafx-vdp/shared';

import { useUpdateFlowMutation } from '@/api/hooks.js';
import { Button } from '@/components/ui/button.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog.js';
import { Input } from '@/components/ui/input.js';
import { Label } from '@/components/ui/label.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.js';
import { toast } from '@/components/ui/sonner.js';

interface Props {
  flow: Flow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const PICKUP_STRATEGY_OPTIONS: { value: PickupStrategy; label: string }[] = [
  { value: 'none', label: 'No action' },
  { value: 'delete', label: 'Delete picked-up file' },
  { value: 'move', label: 'Move picked-up file' },
];

const DELIVERY_STRATEGY_OPTIONS: { value: DeliveryStrategy; label: string }[] = [
  {
    value: 'source-suffix',
    label: 'Reuse pickup filename + suffix',
  },
  { value: 'custom', label: 'Custom pattern' },
];

export function EditFlowDialog({ flow, open, onOpenChange }: Props) {
  const update = useUpdateFlowMutation();
  const [templateId, setTemplateId] = useState('');
  const [outputSettingsId, setOutputSettingsId] = useState('');
  const [pickupPath, setPickupPath] = useState('');
  const [pickupRegex, setPickupRegex] = useState('');
  const [pickupStrategy, setPickupStrategy] = useState<PickupStrategy>('none');
  const [pickedUpPath, setPickedUpPath] = useState('');
  const [deliveryPath, setDeliveryPath] = useState('');
  const [deliveryStrategy, setDeliveryStrategy] = useState<DeliveryStrategy>('source-suffix');
  const [filenameConvention, setFilenameConvention] = useState('');
  const [notificationEmails, setNotificationEmails] = useState('');

  const { reset } = update;
  useEffect(() => {
    if (open && flow) {
      setTemplateId(flow.templateId);
      setOutputSettingsId(flow.outputSettingsId ?? '');
      setNotificationEmails(flow.notificationEmails.join(', '));
      if (flow.inputConfig.engine === 'sftp') {
        setPickupPath(flow.inputConfig.path);
        setPickupRegex(flow.inputConfig.pickupRegex);
        setPickupStrategy(resolvePickupStrategy(flow.inputConfig));
        setPickedUpPath(flow.inputConfig.pickedUpPath ?? '');
      } else {
        setPickupPath('');
        setPickupRegex('');
        setPickupStrategy('none');
        setPickedUpPath('');
      }
      if (flow.outputConfig.engine === 'sftp') {
        setDeliveryPath(flow.outputConfig.path);
        setDeliveryStrategy(resolveDeliveryStrategy(flow.outputConfig));
      } else {
        setDeliveryPath('');
        setDeliveryStrategy('source-suffix');
      }
      setFilenameConvention(flow.filenameConvention ?? '');
    } else if (!open) {
      reset();
    }
  }, [open, flow, reset]);

  if (!flow) return null;

  const hasSftpInput = flow.inputEngine === 'sftp' && flow.inputConfig.engine === 'sftp';
  const hasSftpOutput = flow.outputEngine === 'sftp' && flow.outputConfig.engine === 'sftp';

  const handleSave = async () => {
    try {
      await update.mutateAsync({
        flowId: flow.id,
        input: {
          templateId,
          outputSettingsId: outputSettingsId.trim() || null,
          notificationEmails,
          ...(hasSftpInput
            ? {
                pickupPath: pickupPath.trim(),
                pickupRegex: pickupRegex.trim(),
                pickupStrategy,
                ...(pickupStrategy === 'move' ? { pickedUpPath: pickedUpPath.trim() } : {}),
              }
            : {}),
          ...(hasSftpOutput
            ? {
                deliveryPath: deliveryPath.trim(),
                deliveryStrategy,
              }
            : {}),
          filenameConvention: filenameConvention.trim(),
        },
      });
      toast.success('Flow updated', {
        description: `${flow.name} was saved.`,
      });
      onOpenChange(false);
    } catch (e) {
      toast.error('Save failed', {
        description: e instanceof Error ? e.message : 'Unknown error',
      });
    }
  };

  const canSave =
    templateId.trim().length > 0 &&
    (!hasSftpInput ||
      (pickupPath.trim().length > 0 &&
        pickupRegex.trim().length > 0 &&
        (pickupStrategy !== 'move' || pickedUpPath.trim().length > 0))) &&
    (!hasSftpOutput || deliveryPath.trim().length > 0) &&
    (deliveryStrategy === 'source-suffix' || filenameConvention.trim().length > 0) &&
    !update.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit {flow.name}</DialogTitle>
          <DialogDescription>
            Update GraFx template settings, SFTP folder paths, pickup handling, and notification
            recipients.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-flow-template-id">Template ID</Label>
            <Input
              id="edit-flow-template-id"
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              className="font-mono text-xs"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-flow-output-settings-id">Output settings ID</Label>
            <Input
              id="edit-flow-output-settings-id"
              value={outputSettingsId}
              onChange={(e) => setOutputSettingsId(e.target.value)}
              className="font-mono text-xs"
            />
          </div>

          {hasSftpInput ? (
            <>
              <div className="space-y-2">
                <Label htmlFor="edit-flow-pickup-path">Pickup folder</Label>
                <Input
                  id="edit-flow-pickup-path"
                  value={pickupPath}
                  onChange={(e) => setPickupPath(e.target.value)}
                  placeholder="/pickup"
                  className="font-mono text-xs"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-flow-pickup-regex">Pickup filename regex</Label>
                <Input
                  id="edit-flow-pickup-regex"
                  value={pickupRegex}
                  onChange={(e) => setPickupRegex(e.target.value)}
                  className="font-mono text-xs"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-flow-pickup-strategy">After pickup</Label>
                <Select
                  value={pickupStrategy}
                  onValueChange={(value) => setPickupStrategy(value as PickupStrategy)}
                >
                  <SelectTrigger id="edit-flow-pickup-strategy">
                    <SelectValue placeholder="Choose post-pickup action" />
                  </SelectTrigger>
                  <SelectContent>
                    {PICKUP_STRATEGY_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  What happens to the remote file after it has been downloaded successfully.
                </p>
              </div>
              {pickupStrategy === 'move' ? (
                <div className="space-y-2">
                  <Label htmlFor="edit-flow-picked-up-path">Picked-up folder path</Label>
                  <Input
                    id="edit-flow-picked-up-path"
                    value={pickedUpPath}
                    onChange={(e) => setPickedUpPath(e.target.value)}
                    placeholder="/processed"
                    className="font-mono text-xs"
                  />
                </div>
              ) : null}
            </>
          ) : null}

          {hasSftpOutput ? (
            <>
              <div className="space-y-2">
                <Label htmlFor="edit-flow-delivery-path">Delivery folder</Label>
                <Input
                  id="edit-flow-delivery-path"
                  value={deliveryPath}
                  onChange={(e) => setDeliveryPath(e.target.value)}
                  placeholder="/delivery"
                  className="font-mono text-xs"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-flow-delivery-strategy">Delivery naming</Label>
                <Select
                  value={deliveryStrategy}
                  onValueChange={(value) => setDeliveryStrategy(value as DeliveryStrategy)}
                >
                  <SelectTrigger id="edit-flow-delivery-strategy">
                    <SelectValue placeholder="Choose delivery naming" />
                  </SelectTrigger>
                  <SelectContent>
                    {DELIVERY_STRATEGY_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  After GraFx returns a ZIP, each PDF is renamed before SFTP upload.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-flow-filename-convention">Output filename pattern</Label>
                <Input
                  id="edit-flow-filename-convention"
                  value={filenameConvention}
                  onChange={(e) => setFilenameConvention(e.target.value)}
                  placeholder={
                    deliveryStrategy === 'source-suffix'
                      ? DEFAULT_SOURCE_SUFFIX_FILENAME_CONVENTION
                      : '{Campaign_Name}_TPI_{Smart_Template_Name}_{Campaign_Code}_{NNNN}.pdf'
                  }
                  className="font-mono text-xs"
                />
                <p className="text-xs text-muted-foreground">
                  {deliveryStrategy === 'source-suffix' ? (
                    <>
                      Reuses the pickup CSV basename via {'{sourceBasename}'}. Use {'{NNNN}'} for
                      the PDF index and {'{TTTT}'} for the total count (e.g.{' '}
                      {'{NNNN}-{TTTT}'} → 0001-0006). Leave empty for the default{' '}
                      <code className="font-mono text-[11px]">_VDP_&#123;NNNN&#125;-&#123;TTTT&#125;</code>{' '}
                      suffix.
                    </>
                  ) : (
                    <>
                      Applied to each PDF after GraFx output. Use named groups from the pickup
                      regex (e.g. {'{Campaign_Name}'}), {'{sourceBasename}'}, {'{NNNN}'},{' '}
                      {'{TTTT}'}, and date tokens like {'{yyyymmdd}'}.
                    </>
                  )}
                </p>
              </div>
            </>
          ) : null}

          <div className="space-y-2">
            <Label htmlFor="edit-flow-notification-emails">Notification emails</Label>
            <Input
              id="edit-flow-notification-emails"
              type="text"
              value={notificationEmails}
              onChange={(e) => setNotificationEmails(e.target.value)}
              placeholder="ops@example.com, alerts@example.com"
            />
            <p className="text-xs text-muted-foreground">
              Comma-separated recipients for run success and failure emails.
            </p>
          </div>
        </div>

        {update.isError ? (
          <p className="text-sm text-destructive">
            {update.error instanceof Error ? update.error.message : 'Update failed.'}
          </p>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!canSave}>
            {update.isPending ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
